import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

// Types for simulation creator
export type SimulationScenario = {
  industry: string
  companyDescription: string
  companyChallenges: string
  participantRole: string
  participantObjectives: string
}

export type GenerationOptions = {
  aiProvider?: string
  aiModel?: string
  maxTokens?: number
  temperature?: number
  apiKey?: string
}

export type GenerateSimulationRequest = {
  type: 'FULL_SIMULATION' | 'SCENARIO_ONLY' | 'STAKEHOLDERS_ONLY' | 'MESSAGES_ONLY' | 'ACTIONS_ONLY' | 'INFORMATION_ONLY'
  scenario: SimulationScenario
  prompt?: string
  templateId?: string  // ID of specific template to use instead of A/B testing
  options?: GenerationOptions
}

export type GenerationMetadata = {
  aiProvider: string
  aiModel: string
  tokensUsed: number
  estimatedCost: number
  processingTimeMs: number
  timestamp: string
  promptVersion: string
}

export type StakeholderProfile = {
  id: string
  name: string
  position: string
  description: string
}

export type SimulationMessage = {
  id: string
  from: string
  to: string
  subject: string
  body: string
  timestamp: string
  response?: string | null
  validateResponse?: string | null
  type?: string | null
}

export type ActionTree = {
  id: string
  name: string
  categories: unknown[]
}

export type InformationTree = {
  categories: unknown[]
}

export type SimulationResponse = {
  id: number
  response: string
  time: string
}

export type SimulationCategory = {
  id: number
  text: string
  icon: string | null
  order: number
  actionSentence: string | null
  planSentence: string | null
  isInfoSearch: number
  infoSearchCategory: number | null
  measureReference: string | null
  reasonsToNotDelete: Array<{ simulation: string }>
  categories: SimulationCategory[]
  responses?: SimulationResponse[] | null
}

export type FinalSimulationEvent = {
  id: number
  name: string
  description: string
  category: number
  dateTime: string
  type: string
  prompt: string | null
  response: string
  validateResponse: string | null
  trigger: string | null
  elapsedTime: string
  action: number
  order: number
  options: null
}

export type FinalSimulationPlot = {
  id: number
  name: string
  description: string
  photoImage: string
  listImage: string
  backgroundImage: string
  status: string
  scut: string
  age: string
  gender: string
  length: string
  events: FinalSimulationEvent[]
}

export type FinalSimulationData = {
  id: number
  name: string
  description: string
  image: {
    ref: string
    url: string
  }
  template: {
    id: number
    name: string
    settings: string
  }
  informationSearchCategory: number
  emergencyStart: string
  emergencyEnd: string
  length: string
  actionset: {
    id: number
    name: string
    isNewEngine: "Yes"
    categories: SimulationCategory[]
  }
  map: {
    grid: {
      columns: number
      lines: number
    }
    image: {
      ref: string
      url: string
    }
    plots: Record<string, FinalSimulationPlot>
  }
  videoUrl?: string | null
  allowPause?: boolean
  promptPlan?: boolean
  goalStart?: string | null
  goalEnd?: string | null
}

export type GenerationListItem = {
  id: string
  simulationId: string
  type: 'FULL_SIMULATION' | 'SCENARIO_ONLY' | 'STAKEHOLDERS_ONLY' | 'MESSAGES_ONLY' | 'ACTIONS_ONLY' | 'INFORMATION_ONLY'
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED'
  generatedContent?: unknown
  prompt?: string
  userInputs?: unknown
  aiProvider?: string
  aiModel?: string
  tokensUsed?: number
  estimatedCost?: number
  processingTimeMs?: number
  errorMessage?: string
  contentHash?: string
  isDuplicate?: boolean
  duplicateOf?: string
  isSelected?: boolean
  createdAt: string
  completedAt?: string
}

export type GenerationListResponse = {
  generations: GenerationListItem[]
}

export type SelectGenerationRequest = {
  simulationId: string
  generationId: string
}

export type SelectGenerationResponse = {
  success: boolean
  selectedGeneration: string
}

export type GenerateSimulationResponse = {
  generationId: string
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED'
  content?: {
    stakeholders?: StakeholderProfile[]
    messages?: SimulationMessage[]
    actionTree?: ActionTree
    informationTree?: InformationTree
    questionAnswers?: unknown
  }
  metadata?: GenerationMetadata
  error?: string
}

export type CreateSimulationRequest = {
  name: string
  description?: string
  industry?: string
  status?: 'DRAFT' | 'IN_PROGRESS' | 'COMPLETED' | 'PUBLISHED' | 'ARCHIVED'
}

export type UpdateSimulationRequest = CreateSimulationRequest & {
  scenario?: SimulationScenario
  settings?: {
    estimatedDuration: number | null
    emergencyStartTime?: string
    emergencyEndTime?: string
    totalLength?: string
    difficulty: 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED' | 'EXPERT'
  }
}

export type SimulationListItem = {
  id: string
  name: string
  description?: string
  status: 'DRAFT' | 'IN_PROGRESS' | 'COMPLETED' | 'PUBLISHED' | 'ARCHIVED'
  difficulty: 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED' | 'EXPERT'
  industry?: string
  tags?: string[]
  createdAt: string
  updatedAt: string
  lastGeneratedAt?: string
  createdBy: {
    id: number
    firstName?: string
    lastName?: string
    email: string
  }
}

export type SimulationDetail = SimulationListItem & {
  scenario?: {
    industry: string
    companyDescription: string
    companyChallenges: string
    participantRole: string
    participantObjectives: string
  }
  settings?: {
    estimatedDuration: number | null
    emergencyStartTime: string | null
    emergencyEndTime: string | null
    totalLength: string | null
    difficulty: 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED' | 'EXPERT'
  }
  // Generated content
  questionAnswers?: unknown
  stakeholders?: unknown
  messages?: unknown
  actionTree?: unknown
  informationTree?: unknown
  finalSimulationData?: FinalSimulationData
  // Raw simulation fields for backward compatibility
  companyDescription?: string
  companyChallenges?: string
  participantRole?: string
  participantObjectives?: string
}

export type AiProvider = {
  id: string
  name: string
  models: string[]
}

export type AiProvidersResponse = {
  providers: AiProvider[]
}



// Types for prompt templates
export type PromptTemplate = {
  id: string
  name: string
  description?: string
  step: string
  version: number
  systemPrompt: string
  userPrompt: string
  variables?: Record<string, unknown>
  isActive: boolean
  usageCount: number
  successRate?: number
  avgTokens?: number
  avgCost?: number
  createdBy: number
  organizationId?: number
  parentId?: string
  createdAt: string
  updatedAt: string
}

export type CreatePromptTemplateRequest = {
  name: string
  description?: string
  step: string
  systemPrompt: string
  userPrompt: string
  variables?: Record<string, unknown>
  parentId?: string
}

export type UpdatePromptTemplateRequest = {
  name?: string
  description?: string
  systemPrompt?: string
  userPrompt?: string
  variables?: Record<string, unknown>
  isActive?: boolean
}

// Types for measure mapping
export type MeasureInfo = {
  id: number
  measureName: string
  measureCode?: string
  description?: string
  category?: string
}

export type ActionCategory = {
  text: string
  measureIds?: number[]
  categories?: ActionCategory[]
}

export type ClassifyMeasuresRequest = {
  classificationMode: 'embedding' | 'openai-reasoning' | 'claude-reasoning'
  forceReclassify?: boolean
  apiKey?: string
}

export type ClassifyMeasuresResponse = {
  success: boolean
  actions: ActionCategory[]
  metadata: {
    mode: string
    aiProvider?: string
    aiModel?: string
    actionsClassified: number
    measuresAssigned: number
    processingTimeMs: number
  }
}

export type UpdateActionMeasuresRequest = {
  updates: Array<{
    actionPath: string
    measureIds: number[]
  }>
}

export type UpdateActionMeasuresResponse = {
  success: boolean
  updatedCount: number
}

// Simulation Review Types
export type ActionNodeWithMeasures = {
  id: number | string
  text: string
  measureReference: string | null
  measureIds?: number[]
  actionSentence?: string | null
  planSentence?: string | null
  isInfoSearch?: number
  categories?: ActionNodeWithMeasures[]
}

export type SimulationReviewAuditInfo = {
  actionsWithMeasures: number
  actionsWithoutMeasures: number
  totalActions: number
  totalMessages: number
  totalStakeholders: number
}

export type SimulationReviewResponse = {
  id: string
  name: string
  description?: string
  status: 'DRAFT' | 'IN_PROGRESS' | 'COMPLETED' | 'PUBLISHED' | 'ARCHIVED'
  difficulty: 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED' | 'EXPERT'
  createdAt: string
  updatedAt: string
  createdBy: {
    id: number
    firstName?: string
    lastName?: string
    email: string
  }
  scenario: {
    industry: string
    companyDescription: string
    companyChallenges: string
    participantRole: string
    participantObjectives: string
  } | null
  settings: {
    estimatedDuration: number | null
    emergencyStartTime: string | null
    emergencyEndTime: string | null
    totalLength: string | null
    difficulty: 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED' | 'EXPERT'
  }
  stakeholders: unknown[]
  messages: unknown[]
  actionTree: ActionNodeWithMeasures[]
  auditInfo: SimulationReviewAuditInfo
}

// API configuration
const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3030'

export const simulationCreatorApi = createApi({
  reducerPath: 'simulationCreatorApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseUrl}/saas`,
    credentials: 'include',
  }),
  tagTypes: ['Simulation', 'Generation', 'PromptTemplate'],
  endpoints: (builder) => ({
    // List simulations
    listSimulations: builder.query<{
      data: SimulationListItem[]
      pagination: {
        page: number
        limit: number
        total: number
        pages: number
      }
    }, { page?: number; limit?: number; search?: string }>({
      query: ({ page = 1, limit = 20, search } = {}) => ({
        url: 'simulations',
        params: { page, limit, search },
      }),
      providesTags: ['Simulation'],
    }),

    // Create simulation
    createSimulation: builder.mutation<SimulationListItem, CreateSimulationRequest>({
      query: (body) => ({
        url: 'simulations',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Simulation'],
    }),

    // Get simulation by ID
    getSimulation: builder.query<SimulationDetail, string>({
      query: (id) => `simulations/${id}`,
      providesTags: (result, error, id) => [{ type: 'Simulation', id }],
    }),

    // Get simulation for review (with all metadata exposed)
    getSimulationForReview: builder.query<SimulationReviewResponse, string>({
      query: (id) => `simulations/${id}/review`,
      providesTags: (result, error, id) => [{ type: 'Simulation', id }],
    }),

    // Generate simulation content
    generateSimulation: builder.mutation<GenerateSimulationResponse, { 
      simulationId: string
      request: GenerateSimulationRequest 
    }>({
      query: ({ simulationId, request }) => ({
        url: `simulations/${simulationId}/generate`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (result, error, { simulationId }) => [
        { type: 'Simulation', id: simulationId },
        'Generation'
      ],
    }),

    // Get generation status
    getGenerationStatus: builder.query<GenerateSimulationResponse, {
      simulationId: string
      generationId: string
    }>({
      query: ({ simulationId, generationId }) => 
        `simulations/${simulationId}/generations/${generationId}`,
      providesTags: (result, error, { generationId }) => [
        { type: 'Generation', id: generationId }
      ],
    }),

    // Poll generation status (for real-time updates)
    pollGenerationStatus: builder.query<GenerateSimulationResponse, {
      simulationId: string
      generationId: string
    }>({
      query: ({ simulationId, generationId }) => 
        `simulations/${simulationId}/generations/${generationId}`,
      providesTags: (result, error, { generationId }) => [
        { type: 'Generation', id: generationId }
      ],
    }),

    // Update simulation
    updateSimulation: builder.mutation<SimulationListItem, {
      id: string
      updates: Partial<UpdateSimulationRequest>
    }>({
      query: ({ id, updates }) => ({
        url: `simulations/${id}`,
        method: 'PUT',
        body: updates,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Simulation', id },
        'Simulation'
      ],
    }),

    // Delete simulation
    deleteSimulation: builder.mutation<void, string>({
      query: (id) => ({
        url: `simulations/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Simulation'],
    }),

    // Get AI providers
    getAiProviders: builder.query<AiProvidersResponse, void>({
      query: () => 'simulations/ai/providers',
    }),


    // Prompt Template endpoints
    getTemplatesForStep: builder.query<{ success: boolean; templates: PromptTemplate[] }, string>({
      query: (step) => `prompt-templates/step/${step}`,
      providesTags: ['PromptTemplate'],
    }),

    getActiveTemplate: builder.query<{ success: boolean; template: PromptTemplate | null }, string>({
      query: (step) => `prompt-templates/step/${step}/active`,
      providesTags: ['PromptTemplate'],
    }),

    createPromptTemplate: builder.mutation<{ success: boolean; template: PromptTemplate }, CreatePromptTemplateRequest>({
      query: (body) => ({
        url: 'prompt-templates',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PromptTemplate'],
    }),

    updatePromptTemplate: builder.mutation<{ success: boolean; template: PromptTemplate }, { id: string; data: UpdatePromptTemplateRequest }>({
      query: ({ id, data }) => ({
        url: `prompt-templates/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['PromptTemplate'],
    }),

    setActiveTemplate: builder.mutation<{ success: boolean; template: PromptTemplate }, string>({
      query: (id) => ({
        url: `prompt-templates/${id}/activate`,
        method: 'PUT',
      }),
      invalidatesTags: ['PromptTemplate'],
    }),

    getPromptTemplateById: builder.query<{ success: boolean; template: PromptTemplate }, string>({
      query: (id) => `prompt-templates/${id}`,
      providesTags: ['PromptTemplate'],
    }),

    deletePromptTemplate: builder.mutation<{ success: boolean; message: string }, string>({
      query: (id) => ({
        url: `prompt-templates/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PromptTemplate'],
    }),

    // Export simulation
    exportSimulation: builder.mutation<Blob, { simulationId: string; format?: string }>({
      query: ({ simulationId, format = 'JSON' }) => ({
        url: `simulations/${simulationId}/export?format=${format}`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
    }),

    // List generations for a simulation
    listGenerations: builder.query<GenerationListResponse, {
      simulationId: string
      step?: string
    }>({
      query: ({ simulationId, step }) => ({
        url: `simulations/${simulationId}/generations`,
        params: step ? { step } : undefined,
      }),
      providesTags: (result, error, { simulationId }) => [
        { type: 'Generation', id: simulationId },
        'Generation'
      ],
    }),

    // Select a generation as active
    selectGeneration: builder.mutation<SelectGenerationResponse, SelectGenerationRequest>({
      query: ({ simulationId, generationId }) => ({
        url: `simulations/${simulationId}/generations/${generationId}/select`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { simulationId }) => [
        { type: 'Simulation', id: simulationId },
        { type: 'Generation', id: simulationId },
        'Generation'
      ],
    }),

    // Measure classification endpoints
    classifyMeasures: builder.mutation<ClassifyMeasuresResponse, {
      simulationId: string
      request: ClassifyMeasuresRequest
    }>({
      query: ({ simulationId, request }) => ({
        url: `simulations/${simulationId}/classify-measures`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (result, error, { simulationId }) => [
        { type: 'Simulation', id: simulationId }
      ],
    }),

    updateActionMeasures: builder.mutation<UpdateActionMeasuresResponse, {
      simulationId: string
      request: UpdateActionMeasuresRequest
    }>({
      query: ({ simulationId, request }) => ({
        url: `simulations/${simulationId}/action-measures`,
        method: 'PUT',
        body: request,
      }),
      invalidatesTags: (result, error, { simulationId }) => [
        { type: 'Simulation', id: simulationId }
      ],
    }),

    // Get all measures (for the measure picker dropdown)
    getAllMeasures: builder.query<{ status: string; data: MeasureInfo[] }, void>({
      query: () => 'measure-guides/measures',
    }),
  }),
})

export const {
  useListSimulationsQuery,
  useCreateSimulationMutation,
  useGetSimulationQuery,
  useGetSimulationForReviewQuery,
  useGenerateSimulationMutation,
  useGetGenerationStatusQuery,
  usePollGenerationStatusQuery,
  useUpdateSimulationMutation,
  useDeleteSimulationMutation,
  useGetAiProvidersQuery,
  useGetTemplatesForStepQuery,
  useGetActiveTemplateQuery,
  useCreatePromptTemplateMutation,
  useUpdatePromptTemplateMutation,
  useSetActiveTemplateMutation,
  useGetPromptTemplateByIdQuery,
  useDeletePromptTemplateMutation,
  useExportSimulationMutation,
  useListGenerationsQuery,
  useSelectGenerationMutation,
  // Measure classification hooks
  useClassifyMeasuresMutation,
  useUpdateActionMeasuresMutation,
  useGetAllMeasuresQuery,
} = simulationCreatorApi
